import { useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableCellHead,
  TableHead,
  TableRow,
} from '~/ui/Table';
import EditIcon from '~/assets/svg/newSvg/edit.svg?react';
import ChevronIcon from '~/assets/svg/newSvg/chevron-right.svg?react';
import i18next from 'i18next';
import cn from 'classnames';
import styles from './UserEventsTable.module.scss';

interface ITableProps {
  data: {
    id: number;
    object_id: string;
    event_name: string;
    event: string;
    created_at: string;
    description_text?: string;
    description?: {
      human_description?: string;
      object_name?: string | null;
      changed_fields?: string[] | null;
      method?: string;
      url?: string;
    } | null;
    type: { display_name: string; id: number };
  }[];
  sort: { field: string; direction: string };
  setSort: ({ field, direction }: { field: string; direction: string }) => void;
}

const transitionsData: {
  id: number;
  name: string;
  link: string;
  details?: string;
}[] = [
  { id: 1, name: i18next.t('announcements'), link: '/announcements' },
  { id: 2, name: i18next.t('applications'), link: '/requests' },
  { id: 3, name: i18next.t('orders'), link: '/transactions' },
  { id: 4, name: i18next.t('feedback'), link: '/feedback' },
  { id: 12, name: i18next.t('services'), link: '/services' },
  { id: 14, name: i18next.t('menu'), link: '/food', details: 'current' },
];

const ExpandedDescription = ({
  item,
  t,
}: {
  item: ITableProps['data'][number];
  t: (key: string) => string;
}) => {
  const desc = item.description;

  if (desc?.human_description) {
    return (
      <div className={styles.descriptionList}>
        <div className={styles.descriptionItem}>
          <span className={styles.descriptionLabel}>{t('action')}</span>
          <span className={styles.descriptionValue}>{desc.human_description}</span>
        </div>
        {desc.object_name && (
          <div className={styles.descriptionItem}>
            <span className={styles.descriptionLabel}>{t('object')}</span>
            <span className={styles.descriptionValue}>{desc.object_name}</span>
          </div>
        )}
        {!!desc.changed_fields?.length && (
          <div className={styles.descriptionItem}>
            <span className={styles.descriptionLabel}>{t('changed_fields_label')}</span>
            <div className={styles.changedFields}>
              {desc.changed_fields.map(field => (
                <span key={field} className={styles.fieldTag}>{field}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (desc?.method && desc?.url) {
    return (
      <div className={styles.descriptionList}>
        <div className={styles.descriptionItem}>
          <span className={styles.descriptionLabel}>{t('action')}</span>
          <span className={styles.descriptionValue}>{item.event_name || item.event}</span>
        </div>
        <div className={styles.descriptionItem}>
          <span className={styles.descriptionLabel}>{t('request_label')}</span>
          <span className={styles.descriptionValue}>
            <span className={styles.methodBadge}>{desc.method}</span> {desc.url}
          </span>
        </div>
      </div>
    );
  }

  return <span>{item.event_name || item.event || '-'}</span>;
};

const EventsTable = ({ data, sort, setSort }: ITableProps) => {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<number | null>(null);

  return (
    <Table>
      <TableHead sort={sort} setSort={setSort}>
        <TableCellHead field="event">{t('event')}</TableCellHead>
        <TableCellHead field="created_at">{t('event_date')}</TableCellHead>
        <TableCellHead field="type_id">{t('event_type')}</TableCellHead>
        <TableCellHead field="type_id">{t('action')}</TableCellHead>
      </TableHead>
      <TableBody>
        {data?.map(item => {
          const currentTransition = transitionsData.find(
            transition => transition.id === item.type.id
          );
          const isExpanded = expandedId === item.id;
          return (
            <>
              <TableRow
                key={item.id}
                onClick={() =>
                  setExpandedId(prev => (prev === item.id ? null : item.id))
                }
                style={cn(styles.row, isExpanded && styles.rowActive)}
              >
                <TableCell>
                  <span className={styles.chevronWrapper}>
                    <ChevronIcon
                      className={cn(
                        styles.chevron,
                        isExpanded && styles.chevronExpanded
                      )}
                    />
                    {item.event_name}
                  </span>
                </TableCell>
                <TableCell>
                  {dayjs(item.created_at).format('DD.MM.YYYY')}
                </TableCell>
                <TableCell>{item.type.display_name}</TableCell>
                <TableCell>
                  {currentTransition ? (
                    <Link
                      to={`${currentTransition.link}/${item.object_id}${
                        currentTransition.details
                          ? `/${currentTransition.details}`
                          : ''
                      }`}
                      onClick={e => e.stopPropagation()}
                    >
                      <EditIcon />
                    </Link>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              {isExpanded && (
                <tr key={`${item.id}-desc`} className={styles.expandedRow}>
                  <td colSpan={4} className={styles.expandedCell}>
                    <ExpandedDescription item={item} t={t} />
                  </td>
                </tr>
              )}
            </>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default EventsTable;
